/**
Author - Brijesh Pandey
Git - https://github.com/brijesh-pandey
**/

import React, {useState} from "react";
import SafeLink from "components/commons/SafeLink";
import {
  button,
  infoline,
  title,
  wrapper
} from "../styledElements";

import {CountryDropdown} from "components/NewPartners/commons";
import OtpVerification from "../OtpVerification";
import PropTypes from "prop-types";
import Spinner from "components/Spinner";
import {WealthyEnv} from "helpers";
import {colors} from "nxtconstants";
import styled from "styled-components";

const propTypes = {
  formData: PropTypes.object,
  handleCountryCodeChange: PropTypes.func,
  handleCredentials: PropTypes.func,
  handleInput: PropTypes.func,
  handleInputTypeChange: PropTypes.func,
  handleKeyDown: PropTypes.func,
  handleOtpChange: PropTypes.func,
  handleResend: PropTypes.func,
  inputRefs: PropTypes.object,
  inputType: PropTypes.oneOf(["phone", "email"]),
  inputTypeEnums: PropTypes.object,
  isLoading: PropTypes.bool,
  isResendingOtp: PropTypes.bool,
  otp: PropTypes.array,
  resetPin: PropTypes.bool,
  selectedCountryCode: PropTypes.object,
  setShowDropdown: PropTypes.func,
  showDropdown: PropTypes.bool,
  userData: PropTypes.object,
  userStep: PropTypes.number,
  verifyOtp: PropTypes.func
};

const downArrow = "https://i.wlycdn.com/wealthy-home-page/partners-dropdown-arrow.svg";

const UserCredentials = ({
  userStep,
  formData,
  handleInput,
  handleCredentials,
  handleOtpChange,
  handleResend,
  resetPin,
  inputType,
  handleInputTypeChange,
  inputTypeEnums,
  selectedCountryCode,
  showDropdown,
  setShowDropdown,
  handleCountryCodeChange,
  userData,
  isLoading,
  otp,
  verifyOtp,
  inputRefs,
  handleKeyDown,
  isResendingOtp,
  setUserStep
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Check if input contains any non-numeric characters
    if (value.length > 0 && /[^0-9]/.test(value)) {
      handleInputTypeChange(inputTypeEnums.email);
    } else if (value.length > 0) {
      handleInputTypeChange(inputTypeEnums.phone);
    } else {
      handleInputTypeChange(null);
    }
    handleInput(e);
  };

  // for auto fill from browser
  const handleInputBlur = (e) => {
    const value = e.target.value;
    if (value) {
      if (!/^\d+$/.test(value)) {
        handleInputTypeChange(inputTypeEnums.email);
        handleInput({target: {name: "email", value}});
      } else {
        handleInputTypeChange(inputTypeEnums.phone);
        handleInput({target: {name: "phoneNumber", value}});
      }
    }
  };

  const handleButtonClick = () => {
    if (userStep === 2) {
      verifyOtp();
    } else {
      handleCredentials();
    }
  };

  return (
    <UserCredentialsWrapper>
      <Title>
        {resetPin ? "Welcome Back!" : "Welcome to Wealthy"}
      </Title>

      <InfoLine>Log in to your <b>Wealthy Partner</b> Dashboard</InfoLine>
      {
        resetPin && (
          <InfoLine>Login to setup new pin.</InfoLine>
        )
      }
      <FieldsWrapper>
        <FormGroup>
          <PhoneNumberInput>
            {
              inputType === inputTypeEnums.phone && (
                <DropDownWrapper onClick={() => setShowDropdown(!showDropdown)}>
                  <SelectedItem className="active" disabled={userStep === 2}>{selectedCountryCode.code}</SelectedItem>
                  {userStep !== 2 && (
                    <CountryDropdown
                      callback={handleCountryCodeChange}
                      selectedItem={selectedCountryCode.code}
                      setShowDropdown={setShowDropdown}
                      showDropdown={showDropdown}
                    />
                  )}
                  <IconDd
                    $showDropdown={showDropdown}
                    alt="Dropdown arrow"
                    key={showDropdown}
                    src={downArrow}
                  />
                </DropDownWrapper>
              )
            }

            <InputField
              className={`${inputType === inputTypeEnums.phone ? "input--phone-number" : ""}`}
              disabled={userStep === 2}
              name={inputType === inputTypeEnums.phone ? "phoneNumber" : "email"}
              placeholder="Enter Phone Number / Email"
              type="text"
              value={inputType === inputTypeEnums.phone ? formData?.phoneNumber?.replace(selectedCountryCode.code, "") : formData?.email}
              onBlur={handleInputBlur}
              onChange={handleInputChange}
            />
          </PhoneNumberInput>
        </FormGroup>
        {inputType === inputTypeEnums.email && (
          <FormGroup>
            <InputWrapper>
              <InputField
                name="password"
                placeholder="Enter Password"
                type={showPassword ? "text" : "password"}
                value={formData?.password}
                onChange={handleInput}
              />
              <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                <PasswordIcon
                  alt={showPassword ? "Hide password" : "Show password"}
                  src="https://i.wlycdn.com/wealthy-home-page/show-password-eye-icon.svg"

                />
              </PasswordToggle>
            </InputWrapper>

            <ForgotPassword>
              Forgot Password? <span onClick={() => setUserStep(4)}>Reset</span>
            </ForgotPassword>
          </FormGroup>
        )}

        {userStep === 2 && (
          <OtpVerification
            handleKeyDown={handleKeyDown}
            handleOtpChange={handleOtpChange}
            handleResend={handleResend}
            inputRefs={inputRefs}
            isResendingOtp={isResendingOtp}
            otp={otp}
            userData={userData}
            userStep={userStep}
          />
        )}

      </FieldsWrapper>

      <SubmitCredentials
        disabled={(userStep === 1 && (
          (!inputType) ||
            (inputType === inputTypeEnums.phone && (!formData.phoneNumber || formData.phoneNumber.trim() === "")) ||
            (inputType === inputTypeEnums.email && (
              !formData.email || formData.email.trim() === "" ||
              !formData.password || formData.password.trim() === ""
            ))
        )) ||
          (userStep === 2 && otp.some(digit => digit === ""))}
        onClick={handleButtonClick}
      >
        {isLoading ? <Spinner /> : userStep === 2 ? "Verify & Login" : "Login as Partner"}
      </SubmitCredentials>

      {
        userStep === 1 && (
          <JoinWealthy>
            <JoinWealthyText>
              Join us as Wealth Partner! <SafeLink
                href={`${WealthyEnv.URL}/partner#register`}
                rel="noopener noreferrer"
                target="_blank"
              >
                Sign Up
              </SafeLink>
            </JoinWealthyText>
            {/* <JoinWealthyText className="light">
              By Continuing, you agree to our <SafeLink>T&C</SafeLink>, <SafeLink>Privacy Policy</SafeLink> & <SafeLink>Tariff Rates</SafeLink>
            </JoinWealthyText> */}

            <TermsAndConditionsContainer>
              <CaptchaText>
                I agree to Wealthy Partner's Terms of services
                <br /> and &nbsp;
                <CaptchaTextLink
                  href="https://www.buildwealth.in/partners-privacy"
                  rel="noopener noreferrer"
                  target="_blank"
                >Privacy Policy </CaptchaTextLink> This site is protected by reCAPTCHA
                and the Google <CaptchaTextLink
                  href="https://policies.google.com/privacy"
                  rel="noopener noreferrer"
                  target="_blank"
                >Privacy Policy</CaptchaTextLink> and <CaptchaTextLink
                  href="https://policies.google.com/terms"
                  rel="noopener noreferrer"
                  target="_blank"
                >Terms of Service</CaptchaTextLink> apply.
              </CaptchaText>
            </TermsAndConditionsContainer>
          </JoinWealthy>
        )
      }
    </UserCredentialsWrapper>
  );
};

const TermsAndConditionsContainer = styled.div`
  margin-left: 1.5rem;
  margin-right: 1.5rem;
  margin-top: 1.5rem;

  @media  (max-width:768px) {
    margin: 0;
  }
`;

const CaptchaTextLink = styled.a`
    color: ${colors.primary};
    font-size: 1.3rem;
    font-weight: 400;
    font-family: Lato;
    font-style: italic;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
`;

const CaptchaText = styled.p`
    color: #7E7E7E;
    text-align: center;
    font-size: 1.3rem;
    font-family: Lato;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
`;


const PhoneNumberInput = styled.div`
  position: relative;
  display: flex;

  .input--phone-number {
    width: calc(100% - 5rem);
    position: relative;
    border-left: none;
    border-bottom-left-radius: unset;
    border-top-left-radius: unset;
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 1.5rem;
  }
`;

const ForgotPassword = styled.div`
  margin-top: 1rem;
  text-align: center;
  font-size: 1.6rem;
  font-weight: 400;
  font-family: Lato;

  span {
    color: ${colors.primary};
    cursor: pointer;
    font-weight: 500;

  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    span {
      font-size: 1.4rem;
    }
  }
`;

const DropDownWrapper = styled.div`
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.8rem;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.3);
  outline-width: 0px;
  border-top-right-radius: unset !important;
  border-bottom-right-radius: unset !important;
  border-radius: 1.2rem;
  margin: 0 auto;
  gap: 0.5rem;
`;

const SelectedItem = styled.div`
  position: relative;
  font-size: 1.5rem;
  &.active{
    color: #6725F4;
  }

  &[disabled] {
    color: #aaa !important;
    cursor: not-allowed !important  ;
    pointer-events: none !important;
  }
`;

const IconDd = styled.img`
  height: 7px;
  transition: transform 0.1s ease-in-out;
  transform: ${({$showDropdown}) => ($showDropdown ? "rotate(180deg)" : "none")};
`;

const UserCredentialsWrapper = styled(wrapper)``;
const Title = styled(title)``;

const InfoLine = styled(infoline)`
  padding: 0;
  color: #000000;
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  padding-top: 4.4rem;
  width: 100%;

  @media (max-width: 768px) {
    padding-top: 2.4rem;
  }
`;

const FormGroup = styled.div``;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const Icon = styled.img`
  flex-shrink: 0;
  height: 24px;
  padding-right: 1rem;
`;

const InputField = styled.input`
  background-color: #fff;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  padding: 1.4rem 1.4rem;
  font-size: 1.6rem;
  color: ${colors.black};
  width: 100%;
  font-size: 1.8rem;
  font-weight: 400;
  display: block;
  height: 5rem;
  line-height: 1;

  &.input--phone-number, &.input-email {
    width: 100%;
  }

  &:disabled {
    background-color: #f0f0f0;
    color: #aaa;
    cursor: not-allowed;
    pointer-events: none;
  }

  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    font-size: 1.6rem;
    color: ${colors.textGray};
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 1.5rem;

    &::placeholder {
      font-size: 1.5rem;
    }
  }
`;

const PasswordToggle = styled.div`
  position: absolute;
  right: 1.4rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
`;

const PasswordIcon = styled.img`
  width: 2rem;
  height: 2rem;
`;

const SubmitCredentials = styled(button)`
  width: 100%;
  margin: 3rem 0 4.4rem;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }

  &:disabled {
    background: #e0e0e0 !important;
    color: #aaa !important;
    cursor: not-allowed !important;
    border: none !important;
    opacity: 0.7;
  }
`;

const JoinWealthy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* padding: 0 4rem; */
  text-align: center;
  font-family: Lato;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 2rem;
    margin-top: 1rem;
  }
`;

const JoinWealthyText = styled.div`
  font-size: 1.4rem;
  color: ${colors.textGray};

  a {
    color: ${colors.primary};
    text-decoration: none;
    cursor: pointer;
    font-weight: 500;
    font-size: 1.4rem;
    font-style: italic;

    &:hover {
      text-decoration: underline;
    }
  }

  &.light {
    font-size: 1.3rem;
    opacity: 0.8;

    >a {
      font-size: 1.3rem;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;

    >a {
      font-size: 1.2rem;
    }

    .light {
      font-size: 1.2rem;
    }
  }
`;

UserCredentials.propTypes = propTypes;

export default UserCredentials;